/**
 * Forward-looking factors derived from Bloomberg consensus + balance-sheet
 * trailing values. Extracted from factor-pipeline.ts to keep the orchestrator
 * focused on glue. All functions are pure and return primitives or small
 * tuples.
 */
import type { StockData, StockOverride } from '@/lib/types';
import { safe } from './utils';

/** Forward profitability — BBG returns these in percent form; normalize. */
export function fwdProfitability(override?: StockOverride) {
  return {
    fwdRoe: override?.fwdRoe != null ? safe(override.fwdRoe / 100) : null,
    fwdRoa: override?.fwdRoa != null ? safe(override.fwdRoa / 100) : null,
  };
}

/** Forward net margin (derived from raw BBG consensus net income / sales). */
export function fwdNetMargin(override?: StockOverride): number | null {
  if (override?.fwdNi == null || override?.fwdRev == null || override.fwdRev <= 0) return null;
  return safe(override.fwdNi / override.fwdRev);
}

/**
 * Forward Safety — net debt / EBITDA. Inverted in scoring (lower = safer).
 * Non-financials only. Falls back to BBG's direct NET_DEBT_TO_EBITDA mnemonic
 * when the forward derivation fails — closes ~112 standard names where
 * fwdNetDebt or fwdEbitda is null.
 */
export function fwdDebtToEbitda(override?: StockOverride): number | null {
  if (override?.fwdNetDebt != null && override?.fwdEbitda != null && override.fwdEbitda > 0) {
    return safe(override.fwdNetDebt / override.fwdEbitda);
  }
  if (override?.netDebtToEbitdaDirect != null) return safe(override.netDebtToEbitdaDirect);
  return null;
}

/**
 * Forward Shareholder Returns — dividend yield + implied buyback yield.
 *
 * Dividend yield prefers BBG DIVIDEND_INDICATED_YIELD (announcement-aware
 * latest-declared rate × frequency / price). Falls back to fwdDivPerShare /
 * price when DIY is missing. dvdIndicatedYield is stored as percent (0.46 =
 * 0.46%), normalized to decimal.
 *
 * Implied buyback yield prefers TTM realized repurchase $ (BBG
 * BS_TOT_VAL_OF_SHARES_REPURCHASED). This is what the company actually spent
 * buying back stock — divided by market cap, it's the best forward estimator
 * under "program continues at current pace." The analyst-implied-shares
 * fallback severely under-measured hyperscalers (META at −0.3% vs reality
 * ~2.0%) because analyst share-count forecasts lag buyback programs.
 *
 * Non-dividend-payers get 0 instead of null so the pillar z-score treats
 * them as bottom-tier (rather than peer-median-imputing them as "average,"
 * which previously masked non-payers).
 */
export function fwdShareholderReturns(d: StockData, override?: StockOverride) {
  const fwdDividendYield = override?.dvdIndicatedYield != null
    ? safe(override.dvdIndicatedYield / 100)
    : (override?.fwdDivPerShare != null && d.price > 0)
      ? safe(override.fwdDivPerShare / d.price) : null;

  const curSharesMillions = override?.currentSharesM != null && override.currentSharesM > 0
    ? override.currentSharesM
    : (d.marketCap && d.price && d.price > 0 ? (d.marketCap / d.price) / 1_000_000 : null);

  const repurchaseValueTTM = override?.sharesRepurchasedValue ?? null;
  let impliedBuybackYield: number | null;
  if (repurchaseValueTTM != null && Math.abs(repurchaseValueTTM) > 0 && d.marketCap && d.marketCap > 0) {
    // BBG returns BS_TOT_VAL_OF_SHARES_REPURCHASED in millions of USD
    // (consistent with other balance-sheet $-value fields). Scale to raw
    // dollars before dividing by market cap. Positive = bought back stock.
    impliedBuybackYield = safe((repurchaseValueTTM * 1_000_000) / d.marketCap);
  } else if (curSharesMillions != null && curSharesMillions > 0
             && override?.impliedShares1y != null && override.impliedShares1y > 0) {
    impliedBuybackYield = safe((curSharesMillions - override.impliedShares1y / 1_000_000) / curSharesMillions);
  } else {
    impliedBuybackYield = null;
  }

  return { fwdDividendYield, impliedBuybackYield };
}

/**
 * Next-quarter EPS growth — prefers priorYrNextQEps (FQ-3, the prior year
 * of the NEXT estimate quarter). Falls back to priorYrSameQEps (FQ-4, prior
 * year of last reported) when the FQ-3 baseline is missing. The off-by-one
 * mattered on BNS.TO — using priorYrSameQEps produced −20% while the
 * correct FQ-3 baseline yielded around −7%.
 */
export function fwdNextQtrEpsGrowth(override?: StockOverride): number | null {
  const priorEpsForNextQ = override?.priorYrNextQEps ?? override?.priorYrSameQEps;
  if (override?.epsNextQ == null || priorEpsForNextQ == null || Math.abs(priorEpsForNextQ) <= 1e-6) {
    return null;
  }
  return safe((override.epsNextQ - priorEpsForNextQ) / Math.abs(priorEpsForNextQ));
}
